import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo
} from 'react';
import {
	Box,
	Button,
	IconButton,
	InputAdornment,
	OutlinedInput,
	Popover,
	Slider,
	Stack,
	Tooltip,
	Typography
} from '@mui/material';
import { debounce } from '../../utils';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { RgbColorPicker, RgbaColorPicker } from 'react-colorful';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';

export interface RgbColor {
	red: number;
	green: number;
	blue: number;
	alpha?: number;
}

interface Props {
	value: any;
	onChange: (value: RgbColor) => void;
	onConfirm: () => void;
	isRgba: boolean;
	disabled?: boolean;
	name?: string;
	hasError?: boolean;
	id?: string;
	project?: any;
	parentCanvasObject?: any;
	children?: React.ReactNode;
	open?: boolean;
	onClose?: () => void;
	headless?: boolean;
	customAnchorSelector?: string;
}

function toHex(r: number, g: number, b: number, a?: number) {
	const toHexStr = (n: number) => {
		const h = Math.round(n).toString(16);
		return h.length === 1 ? '0' + h : h;
	};
	let hex = `#${toHexStr(r)}${toHexStr(g)}${toHexStr(b)}`;
	if (a !== undefined) {
		hex += toHexStr(a * 255);
	}
	return hex;
}

const formatColorString = (
	r: number,
	g: number,
	b: number,
	a?: number,
	f: 'hex' | 'rgb' = 'hex'
) => {
	if (f === 'hex') return toHex(r, g, b, a);
	if (a !== undefined)
		return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(2)})`;
	return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

export default function RgbColorPickerWidget({
	value,
	onChange,
	onConfirm,
	isRgba,
	disabled,
	hasError,
	id,
	project,
	parentCanvasObject,
	children,
	open,
	onClose,
	headless,
	customAnchorSelector
}: Props) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [format, setFormat] = useState<'hex' | 'rgb'>('hex');

	const safeValue: RgbColor =
		value && typeof value === 'object'
			? value
			: { red: 0, green: 0, blue: 0, ...(isRgba ? { alpha: 1 } : {}) };

	const [localColor, setLocalColor] = useState<RgbColor>(safeValue);

	useEffect(() => {
		setLocalColor(safeValue);
	}, [safeValue.red, safeValue.green, safeValue.blue, safeValue.alpha]);

	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const debouncedOnChange = useMemo(
		() =>
			debounce(
				(newColor: RgbColor) => onChangeRef.current(newColor),
				300
			),
		[]
	);

	const handleColorUpdate = (newColor: RgbColor) => {
		setLocalColor(newColor);
		debouncedOnChange(newColor);
	};

	const { red = 0, green = 0, blue = 0, alpha = 1 } = localColor;

	const displayColor = isRgba
		? `rgba(${red}, ${green}, ${blue}, ${alpha})`
		: `rgb(${red}, ${green}, ${blue})`;

	const [textValue, setTextValue] = useState(
		formatColorString(red, green, blue, isRgba ? alpha : undefined, format)
	);
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		if (!isFocused) {
			setTextValue(
				formatColorString(
					red,
					green,
					blue,
					isRgba ? alpha : undefined,
					format
				)
			);
		}
	}, [red, green, blue, alpha, isRgba, isFocused, format]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(textValue);
		} catch (e) {
			console.error('Failed to copy', e);
		}
	};

	const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const text = e.target.value;
		setTextValue(text);

		const cleanedText = text.trim();
		let match;

		if (
			(match = cleanedText.match(
				/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i
			))
		) {
			setFormat('hex');
			handleColorUpdate({
				red: parseInt(match[1], 16),
				green: parseInt(match[2], 16),
				blue: parseInt(match[3], 16),
				...(isRgba
					? { alpha: match[4] ? parseInt(match[4], 16) / 255 : 1 }
					: {})
			});
		} else if (
			(match = cleanedText.match(
				/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i
			))
		) {
			setFormat('rgb');
			handleColorUpdate({
				red: parseInt(match[1], 10),
				green: parseInt(match[2], 10),
				blue: parseInt(match[3], 10),
				...(isRgba
					? { alpha: match[4] ? parseFloat(match[4]) : 1 }
					: {})
			});
		} else if (
			(match = cleanedText.match(
				/^(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?$/
			))
		) {
			setFormat('rgb');
			handleColorUpdate({
				red: parseInt(match[1], 10),
				green: parseInt(match[2], 10),
				blue: parseInt(match[3], 10),
				...(isRgba
					? { alpha: match[4] ? parseFloat(match[4]) : 1 }
					: {})
			});
		}
	};

	const handlePickerChange = (newColor: any) => {
		handleColorUpdate({
			red: newColor.r,
			green: newColor.g,
			blue: newColor.b,
			...(isRgba ? { alpha: newColor.a } : {})
		});
	};

	const Picker = isRgba ? RgbaColorPicker : RgbColorPicker;
	const pickerColor = isRgba
		? { r: red, g: green, b: blue, a: alpha }
		: { r: red, g: green, b: blue };

	const ColorRow = ({
		label,
		val,
		onChangeRow,
		colorHex
	}: {
		label: string;
		val: number;
		onChangeRow: (v: number) => void;
		colorHex: string;
	}) => (
		<Stack direction="row" spacing={1} alignItems="center">
			<Typography variant="caption" fontWeight="bold" sx={{ width: 14 }}>
				{label}
			</Typography>
			<Slider
				value={val}
				min={0}
				max={255}
				size="small"
				sx={{ color: colorHex, padding: '10px 0', flexGrow: 1 }}
				onChange={(_, v) => onChangeRow(v as number)}
			/>
			<OutlinedInput
				size="small"
				type="number"
				inputProps={{
					min: 0,
					max: 255,
					style: {
						padding: '4px 2px',
						textAlign: 'center',
						fontSize: '12px'
					}
				}}
				sx={{ width: 58, height: 26 }}
				value={Math.round(val)}
				onChange={(e) => {
					let v = parseInt(e.target.value, 10);
					if (isNaN(v)) v = 0;
					v = Math.max(0, Math.min(255, v));
					onChangeRow(v);
				}}
			/>
		</Stack>
	);

	const handlePopupEvent = (e: React.SyntheticEvent) => {
		e.stopPropagation();
		if (e.nativeEvent) {
			e.nativeEvent.stopPropagation();
			e.nativeEvent.stopImmediatePropagation?.();
		}
	};

	const renderPopupContent = () => (
		<Box
			onClick={handlePopupEvent}
			onDoubleClick={handlePopupEvent}
			onContextMenu={handlePopupEvent}
			onMouseDown={handlePopupEvent}
			onMouseUp={handlePopupEvent}
			onPointerDown={handlePopupEvent}
			onPointerUp={handlePopupEvent}
			onKeyDown={handlePopupEvent}
			sx={{
				p: 2,
				width: 250,
				display: 'flex',
				flexDirection: 'column',
				gap: 1.5,
				backgroundColor: 'white',
				borderRadius: 2,
				boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
			}}
		>
			<Box
				sx={{
					width: '100%',
					'& .react-colorful': {
						width: '100%',
						height: 220,
						gap: '8px'
					},
					'& .react-colorful__saturation': {
						borderRadius: '4px !important',
						borderBottom: 'none !important'
					},
					'& .react-colorful__hue': {
						height: '16px !important',
						borderRadius: '4px !important'
					},
					'& .react-colorful__alpha': {
						height: '16px !important',
						borderRadius: '4px !important'
					},
					'& .react-colorful__alpha::before': {
						content: '"Alpha"',
						position: 'absolute',
						left: '8px',
						top: '50%',
						transform: 'translateY(-50%)',
						fontSize: '11px',
						fontWeight: 'bold',
						color: 'rgba(0,0,0,0.6)',
						pointerEvents: 'none',
						zIndex: 2,
						textShadow: '0 0 2px white'
					}
				}}
			>
				<Picker
					color={pickerColor as any}
					onChange={handlePickerChange}
				/>
			</Box>

			<OutlinedInput
				size="small"
				value={textValue}
				onFocus={() => setIsFocused(true)}
				onBlur={() => {
					setIsFocused(false);
					setTextValue(
						formatColorString(
							red,
							green,
							blue,
							isRgba ? alpha : undefined,
							format
						)
					);
				}}
				onChange={handleTextChange}
				endAdornment={
					<InputAdornment position="end">
						<Button
							size="small"
							variant="text"
							onClick={() => {
								const newFormat =
									format === 'hex' ? 'rgb' : 'hex';
								setFormat(newFormat);
								setTextValue(
									formatColorString(
										red,
										green,
										blue,
										isRgba ? alpha : undefined,
										newFormat
									)
								);
							}}
							sx={{
								minWidth: 'auto',
								p: '2px 4px',
								fontSize: '10px',
								color: 'text.secondary',
								fontWeight: 'bold'
							}}
						>
							{format.toUpperCase()}
						</Button>
						<Tooltip title="Copy Color">
							<IconButton
								size="small"
								onClick={handleCopy}
								sx={{ ml: 0.5 }}
							>
								<ContentCopyIcon
									fontSize="small"
									color="action"
								/>
							</IconButton>
						</Tooltip>
					</InputAdornment>
				}
			/>

			<Stack spacing={0}>
				<ColorRow
					label="R"
					val={red}
					onChangeRow={(v) => onChange({ ...safeValue, red: v })}
					colorHex="#ff4444"
				/>
				<ColorRow
					label="G"
					val={green}
					onChangeRow={(v) => onChange({ ...safeValue, green: v })}
					colorHex="#00C851"
				/>
				<ColorRow
					label="B"
					val={blue}
					onChangeRow={(v) => onChange({ ...safeValue, blue: v })}
					colorHex="#33b5e5"
				/>
			</Stack>
		</Box>
	);

	const canvasPopupId = id ? `rgb-color-picker-${id}--popup` : undefined;
	const canvasPopupAnchorId = id
		? `rgb-color-picker-${id}--anchor`
		: undefined;
	const canvasPopupAnchorSelector =
		customAnchorSelector ||
		(canvasPopupAnchorId ? `#${canvasPopupAnchorId}` : undefined);

	const memoizedPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={[]}
				id={canvasPopupId ? canvasPopupId + '--body' : undefined}
			>
				{renderPopupContent()}
			</CanvasPopupBaseComponent>
		),
		[
			red,
			green,
			blue,
			alpha,
			isRgba,
			format,
			textValue,
			pickerColor,
			safeValue,
			handlePickerChange,
			handleTextChange,
			handleCopy,
			onChange,
			canvasPopupId
		]
	);

	const canvasPopup =
		parentCanvasObject &&
		project &&
		canvasPopupId &&
		canvasPopupAnchorSelector
			? useCanvasPopup(
					{
						editor: project,
						id: canvasPopupId,
						anchorQuerySelector: canvasPopupAnchorSelector,
						arrowClasses: ['dropdown-menu-card-arrow'],
						arrowContainerSelector: '#' + canvasPopupId + '--body',
						parentCanvasObject,
						placement: 'bottom'
					},
					memoizedPopupBody,
					[
						red,
						green,
						blue,
						alpha,
						isRgba,
						format,
						textValue,
						safeValue,
						canvasPopupId
					]
				)
			: null;

	useEffect(() => {
		if (open !== undefined && canvasPopup) {
			if (open) {
				canvasPopup.show(true);
			} else {
				canvasPopup.hide();
			}
		}
	}, [open, canvasPopup]);

	// Watch for external hiding of the canvas popup (e.g., clicking on the canvas)
	useEffect(() => {
		if (!open || !canvasPopup || (headless && !parentCanvasObject)) return;

		const interval = setInterval(() => {
			if (canvasPopup.self?.element && open) {
				if (canvasPopup.self.element.style.display === 'none') {
					if (onClose) onClose();
				}
			}
		}, 200);

		return () => clearInterval(interval);
	}, [canvasPopup, open, onClose, headless, parentCanvasObject]);

	const handleConfirm = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (canvasPopup) {
			canvasPopup.hide();
		} else {
			setAnchorEl(null);
		}
		onConfirm();
	};

	if (headless) {
		return !parentCanvasObject ? (
			<Popover
				open={open !== undefined ? open : Boolean(anchorEl)}
				anchorEl={
					anchorEl ||
					(customAnchorSelector
						? document.querySelector(customAnchorSelector)
						: null)
				}
				onClose={() => {
					setAnchorEl(null);
					if (onClose) onClose();
				}}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left'
				}}
				onKeyDown={(e) => {
					e.stopPropagation();
				}}
			>
				{renderPopupContent()}
			</Popover>
		) : null;
	}

	return (
		<Box
			id={canvasPopupAnchorId}
			sx={
				children
					? { display: 'flex', width: '100%', height: '100%' }
					: { width: '100%' }
			}
		>
			{children ? (
				<Box
					sx={{ display: 'flex', width: '100%', height: '100%' }}
					onClick={(e) => {
						handlePopupEvent(e);
						if (open === undefined) {
							if (canvasPopup) {
								canvasPopup.show(true, e.nativeEvent);
							} else {
								setAnchorEl(e.currentTarget);
							}
						}
					}}
				>
					{children}
				</Box>
			) : (
				<OutlinedInput
					fullWidth
					size="small"
					autoFocus
					disabled={disabled}
					error={hasError}
					readOnly
					value={
						isRgba
							? `RGBA(${red}, ${green}, ${blue}, ${alpha.toFixed(2)})`
							: `RGB(${red}, ${green}, ${blue})`
					}
					onClick={(e) => {
						handlePopupEvent(e);
						if (canvasPopup) {
							canvasPopup.show(true, e.nativeEvent);
						} else {
							setAnchorEl(e.currentTarget);
						}
					}}
					startAdornment={
						<InputAdornment position="start">
							<Box
								onClick={(e) => {
									e.stopPropagation();
									if (canvasPopup) {
										canvasPopup.show(true, e.nativeEvent);
									} else {
										setAnchorEl(e.currentTarget);
									}
								}}
								sx={{
									width: 20,
									height: 20,
									borderRadius: 1,
									backgroundColor: displayColor,
									border: '1px solid rgba(0,0,0,0.2)',
									cursor: 'pointer'
								}}
							/>
						</InputAdornment>
					}
					endAdornment={
						<InputAdornment position="end">
							<IconButton
								aria-label="confirm value"
								disabled={disabled || hasError}
								onClick={handleConfirm}
								edge="end"
								style={{
									backgroundColor:
										disabled || hasError
											? 'rgba(0,0,0,0.12)'
											: 'var(--color-main-success)',
									cursor:
										disabled || hasError
											? 'not-allowed'
											: 'pointer',
									width: 26,
									color: 'white',
									height: 26,
									marginRight: 2
								}}
							>
								<CheckIcon />
							</IconButton>
						</InputAdornment>
					}
				/>
			)}

			{!parentCanvasObject && (
				<Popover
					open={open !== undefined ? open : Boolean(anchorEl)}
					anchorEl={anchorEl}
					onClose={() => {
						setAnchorEl(null);
						if (onClose) onClose();
					}}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left'
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left'
					}}
					onKeyDown={(e) => {
						e.stopPropagation();
					}}
				>
					{renderPopupContent()}
				</Popover>
			)}
		</Box>
	);
}
